import { GridFilterModel, GridSortModel } from "@mui/x-data-grid";

export interface CompanyFilterItem {
    id: string;
    name: string;
}

export interface FinancialFilterItem {
    id: string;
}

export interface BuildApplicationsRequestArgs {
    listCode: string;
    withPagination?: boolean;
    pagination: { page: number; pageSize: number };
    fullSearchText: string;
    filters?: GridFilterModel;
    sort?: GridSortModel;
    companiesUser: Array<CompanyFilterItem>;
    financialFilter: Array<FinancialFilterItem>;
}

export interface BuiltApplicationsRequest {
    urlParams: URLSearchParams;
    useMarketplace: boolean;
}

/**
 * Ported verbatim (query-param construction + per-`listCode` status/list_name
 * branching) from `LoanAppDataGrid.tsx`'s `getRequest()`, as a pure,
 * side-effect-free function — no RTK Query triggers, no dispatch — so the v2
 * table can reuse the exact same business logic (status code sets per tab,
 * marketplace vs normal endpoint choice) without touching the legacy grid
 * component's runtime behavior. Keep this in sync with `getRequest()` if the
 * legacy branching ever changes.
 */
export function buildApplicationsRequest(args: BuildApplicationsRequestArgs): BuiltApplicationsRequest | undefined {
    const {
        listCode,
        withPagination = true,
        pagination,
        fullSearchText,
        filters,
        sort,
        companiesUser,
        financialFilter,
    } = args;

    const urlParams = new URLSearchParams();

    if (withPagination) {
        urlParams.append("page", (pagination.page + 1).toString());
        urlParams.append("size", pagination.pageSize.toString());
    }

    if (fullSearchText?.length !== 0) {
        urlParams.append("fullsearch", fullSearchText);
    }

    let withoutOrderBy = true;

    if (filters) {
        filters.items.forEach((item) => {
            if (Object.keys(item).includes("value") === false) return;

            if (typeof item.value === "string" || typeof item.value === "number") {
                urlParams.append("q", `${item.field}:${item.operator}:'${item.value}'`);
            } else if (typeof item.value === "boolean") {
                urlParams.append("q", `${item.field}:${item.operator}:${item.value}`);
            } else {
                const value = (item.value as Array<string>).join(",");
                urlParams.append("q", `${item.field}:${item.operator}:${value}`);
            }
        });
    }

    if (sort) {
        sort.forEach((item) => urlParams.append("order_by", `${item.field}:${item.sort}`));

        if (sort.length !== 0) {
            withoutOrderBy = false;
        }
    }

    if (withoutOrderBy) {
        urlParams.append("order_by", `id_application:desc`);
    }

    let useMarketplace = false;

    financialFilter.forEach((financial) => urlParams.append("financial_inc", financial.id));
    if (listCode === "MTOD") {
        companiesUser.forEach((t) => urlParams.append("company", t.id));
        urlParams.append("list_name", listCode);

        if (companiesUser.at(0)?.name === "PYMIO" && companiesUser.length === 1) {
            useMarketplace = true;
        }
    } else if (listCode === "MSOL") {
        companiesUser.forEach((t) => urlParams.append("company", t.id));

        if (companiesUser.at(0)?.name === "PYMIO" && companiesUser.length === 1) {
            urlParams.append("list_name", "all");
            useMarketplace = true;
        } else {
            urlParams.append("list_name", listCode);
            [
                "CSDUS",
                "CSRUS",
                "CSVTE",
                "CSDEM",
                "CSAEM",
                "CSUDC",
                "CSPDI",
                "CSDAL",
                "CSCDA",
                "CSDAV",
                "CSDOE",
                "CSABA",
                "CSIP1",
                "CSCOF",
                "CSEOF",
                "CSSSA",
                "CSLSA",
                "CSIBA",
                "CSBBC",
            ].forEach((code) => urlParams.append("status", code));
        }
    } else if (listCode === "MREV") {
        urlParams.append("list_name", listCode);
        companiesUser.forEach((t) => urlParams.append("company", t.id));
        ["CSREV", "CREVC", "CRECC", "CSEOF"].forEach((code) => urlParams.append("status", code));
    } else if (listCode === "MPIN") {
        urlParams.append("list_name", listCode);
        urlParams.append("q", "interested:equals:false");
        companiesUser.forEach((t) => urlParams.append("company", t.id));
    } else if (listCode === "MREC") {
        urlParams.append("list_name", listCode);
        companiesUser.forEach((t) => urlParams.append("company", t.id));
        ["CSREC", "CSRER", "CSRDO", "CSRFO", "CSRCO", "CSREO"].forEach((code) => urlParams.append("status", code));
    } else if (listCode === "MAUT") {
        urlParams.append("list_name", listCode);
        companiesUser.forEach((t) => urlParams.append("company", t.id));
        [
            "CSAAO",
            "CSAOL",
            "CSBEX",
            "CSDDI",
            "CSDIC",
            "CSAED",
            "CSEDC",
            "CSECI",
            "CSVCC",
            "CSVCL",
            "CSAVC",
            "CSVDV",
            "CSVYC",
            "CSRIC",
            "CSDDV",
            "CSEVD",
            "CSVDE",
            "CSBAF",
            "CSFRL",
            "CSFIR",
            "CSVDF",
            "CSDIS",
            "CSFIE",
            "CSVVC",
        ].forEach((code) => urlParams.append("status", code));
        urlParams.append("q", "interested:isNot:false");
    } else if (listCode === "MCLI") {
        companiesUser.forEach((t) => urlParams.append("company", t.id));

        // El backend filtra por la etapa "Cliente" a partir de list_name=MCLI
        urlParams.append("list_name", listCode);

        if (companiesUser.at(0)?.name === "PYMIO" && companiesUser.length === 1) {
            // financialFilter.forEach((financial) => urlParams.append("financial_inc", financial.id));
            useMarketplace = true;
        }
    } else if (listCode === "MREVC") {
        urlParams.append("list_name", listCode);
        companiesUser.forEach((t) => urlParams.append("company", t.id));
        ["CSVDE", "CSBAF", "CSFRL", "CSFIR", "CSVDF", "CSDIS", "CSFIE", "CSCLI"].forEach((code) =>
            urlParams.append("status", code),
        );
    } else if (listCode === "MSOLTGT") {
        companiesUser.forEach((t) => urlParams.append("company", t.id));
        // financialFilter.forEach((financial) => urlParams.append("financial_inc", financial.id));
        urlParams.append("list_name", "target");
        useMarketplace = true;
    } else if (listCode === "MSOLOFER") {
        companiesUser.forEach((t) => urlParams.append("company", t.id));
        // financialFilter.forEach((financial) => urlParams.append("financial_inc", financial.id));
        urlParams.append("list_name", "offered");
        useMarketplace = true;
    } else if (listCode === "MSOLOFACP") {
        companiesUser.forEach((t) => urlParams.append("company", t.id));
        // financialFilter.forEach((financial) => urlParams.append("financial_inc", financial.id));
        urlParams.append("list_name", "offers_accepted");
        useMarketplace = true;
    } else if (listCode === "MSOLOFRECH") {
        companiesUser.forEach((t) => urlParams.append("company", t.id));
        // financialFilter.forEach((financial) => urlParams.append("financial_inc", financial.id));
        urlParams.append("list_name", "offers_rejected");
        useMarketplace = true;
    } else if (listCode === "MINAC") {
        urlParams.append("q", "active:equals:false");
        companiesUser.forEach((t) => urlParams.append("company", t.id));
    } else {
        return undefined;
    }

    return { urlParams, useMarketplace };
}
